import { Playbook } from '@/types/playbook/playbook';
import { Bot, CheckCircle, FileText, Mail, Sparkles, User } from 'lucide-react';

export const interactiveFaq: Playbook = {
  meta: {
    id: 'interactive-faq',
    slug: 'interactive-faq',
    categoryId: 'provide-support',
    name: 'The Interactive FAQ',
    tagline: 'Your first line of defense for support questions.',
    description: 'Transform your FAQ into an intelligent, conversational support Genie that answers questions instantly.',
    strategy: 'Use a Genie as an interactive help center. Train it on your knowledge base to answer common questions instantly.',
    benefits: [
      'Reduce support tickets by 70%',
      '24/7 instant answers',
      'Consistent, accurate responses',
      'Free up human teammates for complex issues'
    ],
    targetAudience: ['SaaS companies', 'E-commerce stores', 'Service businesses'],
    estimatedSetupTime: '15 minutes',
    difficulty: 'beginner',
    tags: ['support', 'faq', 'automation', '24/7'],
    isFeatured: true
  },
  seo: {
    metaTitle: 'Interactive FAQ Playbook - Transform Support with Voice Genies',
    metaDescription: 'Reduce support tickets by 70% with a Genie-powered interactive FAQ. Learn how to deploy voice Genies for instant customer support.',
    keywords: ['interactive faq', 'Genie support', 'voice Genie', 'customer support automation', 'reduce support tickets']
  },
  demoConfig: {
    triggerType: 'button',
    message: "Hi! I'm your support Genie. I can help with common questions about shipping, returns, account settings, and more. What would you like to know?",
    position: 'bottom-right'
  },
  demoScript: [
    { speaker: 'agent', message: "Hi! I'm your support Genie. How can I help?" },
    { speaker: 'user', message: "What's your return policy?" },
    { speaker: 'agent', message: "We offer 30-day returns on all items. I can email you a return label right now!" },
    { speaker: 'user', message: "That would be great, thanks!" },
    { speaker: 'agent', message: "Perfect! I've sent the label to your email. Anything else?" }
  ],
  templateConfig: {
    firstMessage: "Hi! I'm here to help. Ask me anything about our products, services, or policies.",
    systemPrompt: "You are a knowledgeable support Genie. Answer questions accurately based on the knowledge base. Be helpful, concise, and friendly.",
    tools: ['knowledge_base_search'],
    knowledgeBaseId: 'default'
  },
  flowSteps: [
    {
      type: 'user',
      icon: User,
      genieIcon: 'user',
      label: 'Customer has urgent question',
      description: 'Visitor needs immediate help with shipping status, return policy, or account access'
    },
    {
      type: 'genie',
      icon: Bot,
      genieIcon: 'genie-lamp',
      label: 'Intelligent knowledge search',
      description: 'Searches entire knowledge base, FAQ, and policy documents in milliseconds'
    },
    {
      type: 'action',
      icon: Sparkles,
      genieIcon: 'settings',
      label: 'Provides comprehensive answer',
      description: 'Delivers specific answer with relevant links, tracking numbers, or next steps'
    },
    {
      type: 'action',
      icon: FileText,
      genieIcon: 'share',
      label: 'Offers related resources',
      description: 'Suggests helpful articles, video tutorials, or downloadable guides'
    },
    {
      type: 'action',
      icon: Mail,
      genieIcon: 'mail',
      label: 'Captures contact for follow-up',
      description: 'Collects email for complex issues requiring human expertise or status updates'
    },
    {
      type: 'result',
      icon: CheckCircle,
      genieIcon: 'smile',
      label: 'Instant 24/7 resolution',
      description: 'Customer gets accurate help immediately, reducing support tickets by 70%'
    }
  ],
  metrics: {
    conversionRate: '85%',
    responseTime: '<1 second',
    satisfactionScore: '4.8/5',
    costSavings: '$50k/month',
    timeToValue: '1 day'
  },
  testimonials: [
    {
      companyName: 'TechFlow Solutions',
      authorName: 'Sarah Chen',
      authorTitle: 'Head of Customer Success',
      quote: 'Our support ticket volume dropped by 73% in the first month. The Interactive FAQ handles all our common questions perfectly.',
      metrics: '73% reduction in tickets'
    }
  ],
  resources: [
    {
      type: 'guide',
      title: 'Setting Up Your Knowledge Base',
      description: 'Step-by-step guide to organizing your FAQs for optimal Genie responses',
      url: '/docs/knowledge-base-setup'
    },
    {
      type: 'video',
      title: 'Interactive FAQ Demo',
      description: 'See the Interactive FAQ in action across different industries',
      url: '/videos/interactive-faq-demo',
      duration: '5 min'
    }
  ]
};